/* eslint-disable @typescript-eslint/no-unused-vars */
import i18nFramework, { i18n as I18N } from 'i18next';
import { initReactI18next } from 'react-i18next';
import HttpApi from 'i18next-http-backend';
import { PartialDeep } from 'type-fest';

import {
  ThemeUtilities,
  Theme,
  theme as defaultTheme,
} from '@chargify/custom-ui-components';
import { Logger, ChargifyLogger } from '@chargify/logger';
import { DataAccess } from '@chargify/data-access';
import { SessionManager } from '@chargify/session-manager';
import { CustomerDetails } from '@chargify/customer-details';
import { BillingHistory } from '@chargify/billing-history';
import { SubscriptionsManager } from '@chargify/subscriptions-manager';

import { API_URL, DEFAULT_LANGUAGE, DEFAULT_LOAD_PATH } from './constants';

import { I18NSettings, Components } from './types';

export interface FactoryOptions {
  /**
   * Components theming
   */
  theme?: PartialDeep<Theme>;
  /**
   * Internationalization settings
   */
  i18nSettings?: I18NSettings;
  /**
   * Base API url
   */
  apiUrl?: string;
  /**
   * Url used to obtain JWT access token
   */
  accessTokenUrl: string;
  /**
   * Authentication handler hook used to pass additional headers to request
   */
  onAuthenticationRequest?: () => Record<string, any>;

  /**
   * Authentication async handler hook used to pass additional headers to request
   */
  onAuthenticationRequestAsync?: () => Promise<Record<string, string>>;
}

class ComponentsSDK {
  /** @type {Theme} */
  public readonly theme: Theme = defaultTheme;

  /** @type {I18N} */
  private i18n: I18N;

  /** @type {SessionManager} */
  private sessionManager: SessionManager;

  /** @type {DataAccess} */
  private dataAccess: DataAccess;

  /** @type {ChargifyLogger} */
  private logger: ChargifyLogger;

  /** @type {string} */
  private apiUrl: string = API_URL;

  /**
   * @constructor
   * @param {string | HTMLElement} container
   */
  constructor({
    theme,
    accessTokenUrl,
    onAuthenticationRequest,
    onAuthenticationRequestAsync,
    i18nSettings,
    apiUrl,
  }: FactoryOptions) {
    this.i18n = this.createI18NInstance(
      i18nSettings || { language: null, loadPath: null }
    );

    if (theme) this.theme = ThemeUtilities.createTheme(theme);
    if (apiUrl) this.apiUrl = apiUrl;

    this.sessionManager = new SessionManager({
      accessTokenUrl,
      apiUrl,
      onAuthenticationRequest,
      onAuthenticationRequestAsync,
    });

    this.dataAccess = new DataAccess({
      sessionManager: this.sessionManager,
      baseUrl: this.apiUrl,
    });

    this.logger = Logger.get('@components-factory');
  }

  /**
   * Creates i18n framework instance
   */
  private createI18NInstance({ loadPath, language }: I18NSettings) {
    return i18nFramework
      .createInstance({
        lng: language || DEFAULT_LANGUAGE,
        fallbackLng: 'en',
        defaultNS: 'common',
        backend: {
          loadPath: loadPath || DEFAULT_LOAD_PATH,
        },
        react: {
          useSuspense: false,
        },
        interpolation: {
          escapeValue: false,
        },
      })
      .use(initReactI18next)
      .use(HttpApi);
  }

  /**
   * Initializes i18n instance
   */
  public initI18N(): void {
    if (!this.i18n.isInitialized) {
      this.logger.info('Initializing i18n instance');
      this.i18n.init({
        ns: ['common'],
      });
    }
  }

  public get i18nInstance() {
    return this.i18n;
  }

  /**
   * Factory responsible for creating components instances
   * @param {Components} component - type of component
   *
   * @return {EmbeddableComponent} component instance ready to be mount in DOM
   */
  create(component: 'billing-history'): BillingHistory;
  create(component: 'customer-details'): CustomerDetails;
  create(component: 'subscriptions-manager'): SubscriptionsManager;
  create(component: Components) {
    this.initI18N();
    this.sessionManager.initialize();
    const componentConfig = {
      dataAccess: this.dataAccess,
      sessionManager: this.sessionManager,
      componentsTheme: this.theme,
      i18n: this.i18nInstance,
    };
    switch (component) {
      case 'subscriptions-manager':
        return new SubscriptionsManager(componentConfig);
      case 'customer-details':
        return new CustomerDetails(componentConfig);
      case 'billing-history':
        return new BillingHistory(componentConfig);
    }
  }
}

export default ComponentsSDK;
